'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';

const categories = [
  {
    id: 1,
    icon: '🙏',
    name: 'Daily Pooja',
    sanskrit: 'Nitya Pooja',
    count: '240+ priests',
    color: 'from-saffron/20 to-orange-50',
    border: 'border-saffron/30',
    glow: 'hover:shadow-orange-200',
    desc: 'Morning & evening rituals',
  },
  {
    id: 2,
    icon: '🏠',
    name: 'Gruhapravesam',
    sanskrit: 'Vastu Shanti',
    count: '180+ priests',
    color: 'from-amber-100 to-yellow-50',
    border: 'border-amber-300/50',
    glow: 'hover:shadow-amber-200',
    desc: 'New home blessings',
  },
  {
    id: 3,
    icon: '💒',
    name: 'Vivaha',
    sanskrit: 'Marriage Rituals',
    count: '320+ priests',
    color: 'from-rose-100 to-pink-50',
    border: 'border-rose-300/40',
    glow: 'hover:shadow-rose-200',
    desc: 'Wedding ceremonies',
  },
  {
    id: 4,
    icon: '👶',
    name: 'Namakarana',
    sanskrit: 'Naming Ceremony',
    count: '150+ priests',
    color: 'from-sky-100 to-blue-50',
    border: 'border-sky-300/40',
    glow: 'hover:shadow-sky-200',
    desc: 'Baby naming rituals',
  },
  {
    id: 5,
    icon: '🌟',
    name: 'Satyanarayan',
    sanskrit: 'Vrat Katha',
    count: '290+ priests',
    color: 'from-yellow-100 to-amber-50',
    border: 'border-yellow-400/40',
    glow: 'hover:shadow-yellow-200',
    desc: 'Prosperity & blessings',
  },
  {
    id: 6,
    icon: '🔥',
    name: 'Homa / Havan',
    sanskrit: 'Agni Rituals',
    count: '200+ priests',
    color: 'from-orange-100 to-red-50',
    border: 'border-orange-300/40',
    glow: 'hover:shadow-orange-200',
    desc: 'Sacred fire ceremonies',
  },
  {
    id: 7,
    icon: '⭐',
    name: 'Navagraha',
    sanskrit: 'Planetary Pooja',
    count: '165+ priests',
    color: 'from-purple-100 to-violet-50',
    border: 'border-purple-300/40',
    glow: 'hover:shadow-purple-200',
    desc: 'Planetary remedies',
  },
  {
    id: 8,
    icon: '🌺',
    name: 'Devi Pooja',
    sanskrit: 'Shakti Upasana',
    count: '210+ priests',
    color: 'from-pink-100 to-rose-50',
    border: 'border-pink-300/40',
    glow: 'hover:shadow-pink-200',
    desc: 'Goddess worship rituals',
  },
  {
    id: 9,
    icon: '🕉️',
    name: 'Shraddha',
    sanskrit: 'Pitru Karma',
    count: '130+ priests',
    color: 'from-slate-100 to-gray-50',
    border: 'border-slate-300/40',
    glow: 'hover:shadow-slate-200',
    desc: 'Ancestral rites',
  },
  {
    id: 10,
    icon: '🎓',
    name: 'Vidyarambha',
    sanskrit: 'Education Ritual',
    count: '90+ priests',
    color: 'from-emerald-100 to-green-50',
    border: 'border-emerald-300/40',
    glow: 'hover:shadow-emerald-200',
    desc: 'Auspicious learning start',
  },
];

export default function RitualCategories() {
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  return (
    <section className="py-16 px-4 sm:px-6 max-w-screen-xl mx-auto">
      {/* Section Header */}
      <div className="text-center mb-12">
        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-saffron mb-3">
          All Sacred Rituals
        </span>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-maroon mb-3">
          Choose Your Ritual
        </h2>
        <p className="text-maroon/60 text-base max-w-xl mx-auto font-body">
          From daily poojas to life milestones — find priests specializing in every Vedic tradition
        </p>
        <div className="mt-4 w-16 h-0.5 gold-gradient mx-auto rounded-full" />
      </div>
      {/* Category Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {categories?.map((cat) => (
          <Link
            key={cat?.id}
            href="/rituals"
            onMouseEnter={() => setHoveredId(cat?.id)}
            onMouseLeave={() => setHoveredId(null)}
            className={`group relative flex flex-col items-center text-center p-5 rounded-2xl border bg-gradient-to-br ${cat?.color} ${cat?.border} card-hover transition-all duration-300 cursor-pointer ${
              hoveredId === cat?.id ? `shadow-xl ${cat?.glow}` : 'shadow-sm'
            }`}
          >
            {/* 3D-style icon container */}
            <div
              className={`relative w-16 h-16 rounded-2xl flex items-center justify-center mb-3 transition-all duration-300 ${
                hoveredId === cat?.id ? 'scale-110' : ''
              }`}
              style={{
                background: 'rgba(255,255,255,0.7)',
                boxShadow: hoveredId === cat?.id
                  ? '0 8px 24px rgba(212, 175, 55, 0.3), inset 0 1px 0 rgba(255,255,255,0.8)'
                  : '0 4px 12px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)',
              }}
            >
              <span className="text-3xl" role="img" aria-label={cat?.name}>{cat?.icon}</span>
              {/* Glow on hover */}
              {hoveredId === cat?.id && (
                <div className="absolute inset-0 rounded-2xl bg-gold/20 animate-glow-pulse" />
              )}
            </div>

            <h3 className="font-display text-sm font-semibold text-maroon leading-tight">{cat?.name}</h3>
            <p className="text-[10px] text-maroon/50 mt-0.5 font-body italic">{cat?.sanskrit}</p>
            <p className="text-[11px] text-maroon/60 mt-2 font-body">{cat?.desc}</p>
            <span className="mt-3 text-[10px] font-semibold text-saffron bg-saffron/10 px-2.5 py-1 rounded-full">
              {cat?.count}
            </span>

            {/* Arrow on hover */}
            <div className={`absolute top-3 right-3 transition-all duration-200 ${hoveredId === cat?.id ? 'opacity-100' : 'opacity-0'}`}>
              <ArrowRight size={14} className="text-saffron" />
            </div>
          </Link>
        ))}
      </div>
      <div className="text-center mt-8">
        <Link
          href="/rituals"
          className="inline-flex items-center gap-2 border border-gold/40 text-maroon hover:bg-gold/10 text-sm font-medium px-6 py-3 rounded-full transition-all duration-200 hover:border-gold"
        >
          View All 50+ Ritual Types
          <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
}